import { useEffect, useRef, useState, type ChangeEvent } from "react";

import AppShell from "../components/AppShell";
import PrintDialog from "../components/PrintDialog";
import SharingTabs from "../components/SharingTabs";
import { CardSkeleton } from "../components/Skeleton";
import { WarningIcon } from "../components/Icons";
import { listAgents, type AgentRow } from "../api/agents";
import { isHostUnavailable } from "../utils/agent";
import {
  clearCardSettings,
  getPrintFileStatus,
  getResetPrinterSettingsStatus,
  getTestPrintStatus,
  listPrinters,
  printFileViaCloud,
  renamePrinter,
  resetPrinterSettings,
  saveCardSettings,
  sharePrinter,
  testPrintPrinter,
  uninstallPrintersFromClients,
  unsharePrinter,
  unshareAllPrinters,
  type CardSettingsInfo,
  type PrinterRow,
  type PrintOptions,
  type PrinterCapabilities,
  type TestPrintStatus,
} from "../api/printers";

const POLL_INTERVAL_MS = 30_000;
// Test-print polling — much faster than the printer-list refresh because
// we're showing the user a live spinner. Cap total wait at TEST_PRINT_TIMEOUT_MS
// so a stuck task surfaces as "no answer" instead of spinning forever.
//
// Worst-case lifecycle from button-click to "succeeded":
//   - up to ~60 s for the agent's next heartbeat to pick up the task
//   - 5–65 s for SumatraPDF to dispatch + WatchJob to confirm
//   - up to ~60 s for the next heartbeat to report the result back
// So we budget 3 minutes — long but matches reality on a heartbeat-paced
// task pipeline. The agent's task_print fast-path is much shorter in
// typical conditions (~30 s end-to-end).
const TEST_PRINT_POLL_INTERVAL_MS = 1_500;
const TEST_PRINT_TIMEOUT_MS = 180_000;

type TestPrintUiState = {
  status: "running" | "ok" | "warn" | "error";
  message: string;
};

export default function PrinterSharing() {
  const [rows, setRows] = useState<PrinterRow[] | null>(null);
  const [agentMap, setAgentMap] = useState<Map<string, AgentRow>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removingAll, setRemovingAll] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupMsg, setCleanupMsg] = useState<string | null>(null);
  // Per-printer test-print UI state — tracked here (not inside PrinterCard)
  // so the result survives a polling refresh that re-renders the card.
  const [testPrintState, setTestPrintState] = useState<Map<string, TestPrintUiState>>(new Map());
  // Same pattern for the cloud-routed "Print a file" flow. Kept in a
  // separate map so a user can run a test print AND a file print on
  // the same card without one banner clobbering the other.
  const [printFileState, setPrintFileState] = useState<Map<string, TestPrintUiState>>(new Map());
  // Same pattern again for "Reset printer settings" — separate map so it
  // can show alongside a test-print/file-print banner rather than replace it.
  const [resetSettingsState, setResetSettingsState] = useState<Map<string, TestPrintUiState>>(new Map());
  // Stays true while this component is mounted. Poll handlers consult it to
  // bail out after the user navigates away — without this they keep hitting
  // the backend for up to 3 minutes per abandoned test and then setState on
  // an unmounted component.
  const mountedRef = useRef(true);

  async function refresh() {
    try {
      const [printerData, agentData] = await Promise.all([listPrinters(), listAgents()]);
      setRows(printerData.results);
      setAgentMap(new Map(agentData.results.map((a) => [a.id, a])));
      setError(null);
    } catch (e) {
      setError(extractError(e));
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function fetchOnce() {
      try {
        const [printerData, agentData] = await Promise.all([listPrinters(), listAgents()]);
        if (!cancelled) {
          setRows(printerData.results);
          setAgentMap(new Map(agentData.results.map((a) => [a.id, a])));
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(extractError(e));
      }
    }
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      mountedRef.current = false;
    };
  }, []);

  async function onShare(row: PrinterRow) {
    setBusyId(row.id);
    setError(null);
    try {
      await sharePrinter(row.id);
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function onUnshare(row: PrinterRow) {
    if (!window.confirm("Stop sharing this printer? Other computers will lose access.")) return;
    setBusyId(row.id);
    setError(null);
    try {
      await unsharePrinter(row.id);
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusyId(null);
    }
  }

  // Returns null on success, an error string on failure. The PrinterCard's
  // inline rename UI surfaces the error next to the input so the user
  // doesn't lose what they typed.
  async function handleRename(row: PrinterRow, newLabel: string): Promise<string | null> {
    try {
      const updated = await renamePrinter(row.id, newLabel);
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === row.id ? { ...r, label: updated.label } : r)) : prev,
      );
      return null;
    } catch (e) {
      return extractError(e);
    }
  }

  function setPrinterTestState(printerId: string, state: TestPrintUiState | null) {
    setTestPrintState((prev) => {
      const next = new Map(prev);
      if (state === null) {
        next.delete(printerId);
      } else {
        next.set(printerId, state);
      }
      return next;
    });
  }

  async function onTestPrint(row: PrinterRow) {
    setPrinterTestState(row.id, { status: "running", message: "Sending test page…" });
    try {
      const ticket = await testPrintPrinter(row.id);
      const finalStatus = await pollTestPrintUntilDone(
        ticket.task_id,
        getTestPrintStatus,
        () => mountedRef.current,
      );
      if (!mountedRef.current) return;
      setPrinterTestState(row.id, interpretTestPrintStatus(finalStatus));
    } catch (e) {
      if (!mountedRef.current || (e instanceof DOMException && e.name === "AbortError")) return;
      setPrinterTestState(row.id, {
        status: "error",
        message: extractError(e, "Couldn't start the test print."),
      });
    }
  }

  function setPrinterFileState(printerId: string, state: TestPrintUiState | null) {
    setPrintFileState((prev) => {
      const next = new Map(prev);
      if (state === null) {
        next.delete(printerId);
      } else {
        next.set(printerId, state);
      }
      return next;
    });
  }

  async function onPrintFile(row: PrinterRow, file: File, copies: number, options: PrintOptions) {
    setPrinterFileState(row.id, {
      status: "running",
      message: `Uploading ${file.name}…`,
    });
    try {
      const ticket = await printFileViaCloud(row.id, file, copies, options);
      setPrinterFileState(row.id, {
        status: "running",
        message: `Printing ${ticket.filename}…`,
      });
      const finalStatus = await pollTestPrintUntilDone(
        ticket.task_id,
        getPrintFileStatus,
        () => mountedRef.current,
      );
      if (!mountedRef.current) return;
      setPrinterFileState(row.id, interpretPrintFileStatus(finalStatus));
    } catch (e) {
      if (!mountedRef.current || (e instanceof DOMException && e.name === "AbortError")) return;
      setPrinterFileState(row.id, {
        status: "error",
        message: extractError(e, "Couldn't print that file."),
      });
    }
  }

  function setPrinterResetState(printerId: string, state: TestPrintUiState | null) {
    setResetSettingsState((prev) => {
      const next = new Map(prev);
      if (state === null) {
        next.delete(printerId);
      } else {
        next.set(printerId, state);
      }
      return next;
    });
  }

  async function onResetSettings(row: PrinterRow) {
    setPrinterResetState(row.id, { status: "running", message: "Resetting printer settings…" });
    try {
      const ticket = await resetPrinterSettings(row.id);
      const finalStatus = await pollTestPrintUntilDone(
        ticket.task_id,
        getResetPrinterSettingsStatus,
        () => mountedRef.current,
      );
      if (!mountedRef.current) return;
      setPrinterResetState(row.id, interpretResetSettingsStatus(finalStatus));
    } catch (e) {
      if (!mountedRef.current || (e instanceof DOMException && e.name === "AbortError")) return;
      setPrinterResetState(row.id, {
        status: "error",
        message: extractError(e, "Couldn't reset that printer's settings."),
      });
    }
  }

  async function onRemoveAllShared() {
    if (
      !window.confirm(
        "Remove all shared printers? Directly connected printers will stay. Other computers will lose access to shared printers.",
      )
    )
      return;
    setRemovingAll(true);
    setError(null);
    try {
      await unshareAllPrinters();
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setRemovingAll(false);
    }
  }

  async function onCleanupInstalled() {
    if (
      !window.confirm(
        "Remove the office printers from every PC's Windows printer list?\n\n" +
          "This fixes the freezing/lag in the Windows print dialog. Your printers stay shared — " +
          "everyone keeps printing through this dashboard's \"Print a file\" button. Nothing is lost.",
      )
    )
      return;
    setCleaningUp(true);
    setCleanupMsg(null);
    setError(null);
    try {
      const { uninstall_tasks_created } = await uninstallPrintersFromClients();
      setCleanupMsg(
        uninstall_tasks_created > 0
          ? `Cleaning up ${uninstall_tasks_created} printer install(s) across your PCs. The freezing should stop within a minute or two.`
          : "Nothing to clean up — no printers are installed on PCs the old way.",
      );
    } catch (e) {
      setError(extractError(e, "Couldn't start the cleanup."));
    } finally {
      setCleaningUp(false);
    }
  }

  const hasShared = rows?.some(
    (r) => r.status === "active" || r.status === "pending" || r.status === "failed",
  );

  const sortedRows = rows ? [...rows].sort((a, b) => printerSortScore(a) - printerSortScore(b)) : null;

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-2xl font-semibold text-slate-900">Sharing</h1>
        <p className="mt-1 text-slate-600">
          Share folders and printers with everyone in the office.
        </p>
        <SharingTabs />

        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Printers</h2>
            <p className="mt-1 text-sm text-slate-600">
              Printers detected on your office computers. Click "Share with the office" to make one
              available to everyone.
            </p>
          </div>
          {hasShared ? (
            <button
              type="button"
              onClick={onRemoveAllShared}
              disabled={removingAll || busyId !== null}
              className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {removingAll ? "Removing…" : "Remove all shared printers"}
            </button>
          ) : null}
        </div>

        {error ? (
          <p role="alert" className="mt-6 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        ) : null}

        {/* Dashboard-only printing guidance + the freeze fix. Printing
            via the Windows print dialog goes over SMB to the host PC,
            which freezes Explorer + the print dialog when the host is
            slow/offline. Steer everyone to "Print a file" here, and
            give the owner a one-click cleanup for the installed printers
            that cause the freeze. */}
        <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-4">
          <h3 className="text-sm font-semibold text-blue-900">Print from here, not the Windows print box</h3>
          <p className="mt-1 text-sm text-blue-900">
            Use the <strong>Print a file</strong> button on each printer below. It works from any computer
            (and phones) and never freezes — unlike printing through the Windows print dialog, which can
            lag or hang when the printer's computer is busy or asleep.
          </p>
          <p className="mt-2 text-sm text-blue-900">
            If you're seeing the print box or File Explorer freeze, click this once to remove the office
            printers from every PC's Windows list. Your printers stay shared here — nothing is lost.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onCleanupInstalled}
              disabled={cleaningUp || busyId !== null}
              className="rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
            >
              {cleaningUp ? "Cleaning up…" : "Fix the freezing (remove printers from PCs)"}
            </button>
            {cleanupMsg ? <span className="text-xs text-blue-800">{cleanupMsg}</span> : null}
          </div>
        </div>

        <div className="mt-6">
          {sortedRows === null ? (
            <CardSkeleton count={3} />
          ) : sortedRows.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-3">
              {sortedRows.map((row) => (
                <PrinterCard
                  key={row.id}
                  row={row}
                  hostAgent={agentMap.get(row.host_agent) ?? null}
                  busy={busyId === row.id}
                  removingAll={removingAll}
                  testPrintState={testPrintState.get(row.id) ?? null}
                  printFileState={printFileState.get(row.id) ?? null}
                  resetSettingsState={resetSettingsState.get(row.id) ?? null}
                  onShare={() => onShare(row)}
                  onUnshare={() => onUnshare(row)}
                  onRename={(label) => handleRename(row, label)}
                  onTestPrint={() => onTestPrint(row)}
                  onClearTestPrintState={() => setPrinterTestState(row.id, null)}
                  onPrintFile={(file, copies, options) => onPrintFile(row, file, copies, options)}
                  onClearPrintFileState={() => setPrinterFileState(row.id, null)}
                  onResetSettings={() => onResetSettings(row)}
                  onClearResetSettingsState={() => setPrinterResetState(row.id, null)}
                  onChanged={refresh}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function PrinterCard({
  row,
  hostAgent,
  busy,
  removingAll,
  testPrintState,
  printFileState,
  resetSettingsState,
  onShare,
  onUnshare,
  onRename,
  onTestPrint,
  onClearTestPrintState,
  onPrintFile,
  onClearPrintFileState,
  onResetSettings,
  onClearResetSettingsState,
  onChanged,
}: {
  row: PrinterRow;
  hostAgent: AgentRow | null;
  busy: boolean;
  removingAll: boolean;
  testPrintState: TestPrintUiState | null;
  printFileState: TestPrintUiState | null;
  resetSettingsState: TestPrintUiState | null;
  onShare: () => void;
  onUnshare: () => void;
  onRename: (newLabel: string) => Promise<string | null>;
  onTestPrint: () => void;
  onClearTestPrintState: () => void;
  onPrintFile: (file: File, copies: number, options: PrintOptions) => void;
  onClearPrintFileState: () => void;
  onResetSettings: () => void;
  onClearResetSettingsState: () => void;
  onChanged: () => Promise<void>;
}) {
  // Hidden file input the "Print a file" button triggers. Refed so we
  // can imperatively reset it after each pick — without that, picking
  // the SAME file twice doesn't fire onChange (browsers dedupe).
  const fileInputRef = useRef<HTMLInputElement>(null);
  function openFilePicker() {
    fileInputRef.current?.click();
  }
  // The file waiting in the print dialog (preview + settings) until Print or Cancel.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  function handleFilePicked(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    // Reset so picking the same file again re-triggers.
    e.target.value = "";
  }
  const isShared = row.status === "active" || row.status === "pending";
  const anyBusy = busy || removingAll;
  const hostName = row.host_agent_label || row.host_agent_hostname;
  // Inline rename state — mirror the AgentCard pattern in MyComputers so
  // the affordance feels consistent across the app.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.label || "");
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function startEdit() {
    setDraft(row.label || "");
    setRenameError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setRenameError(null);
  }

  async function saveEdit() {
    const next = draft.trim();
    if (next === (row.label || "")) {
      cancelEdit();
      return;
    }
    setRenameBusy(true);
    const err = await onRename(next);
    setRenameBusy(false);
    if (err) {
      setRenameError(err);
      return;
    }
    setEditing(false);
  }

  const hostIsOffline = isHostUnavailable(hostAgent);
  const printerDisconnected =
    row.connectivity === "offline" || row.connectivity === "error" || row.connectivity === "unplugged";

  const canPrint = row.status === "active" && !hostIsOffline && !printerDisconnected;
  // Resetting settings is a purely local fix on the host PC — it doesn't
  // need the printer to be shared with the office (unlike canPrint), and
  // it's often the fix FOR a printer showing as disconnected/erroring, so
  // it doesn't require printerDisconnected to be false either. It only
  // needs the host PC reachable to run the task at all.
  const canReset = !hostIsOffline;
  const canShare = (row.status === "detected" || row.status === "failed") && !printerDisconnected;

  return (
    <li
      className={`rounded-2xl border border-slate-200 bg-white p-5 ${
        (isShared && hostIsOffline) || printerDisconnected ? "opacity-70" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {editing ? (
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit();
                        else if (e.key === "Escape") cancelEdit();
                      }}
                      disabled={renameBusy}
                      maxLength={120}
                      placeholder={row.printer_name}
                      aria-label="Printer name"
                      className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-base font-semibold text-slate-900 focus:border-slate-500 focus:outline-none disabled:opacity-60"
                    />
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={renameBusy}
                      className="no-tap-highlight shrink-0 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                    >
                      {renameBusy ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={renameBusy}
                      className="no-tap-highlight shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                  {renameError ? (
                    <p className="mt-1 text-xs text-red-600" role="alert">{renameError}</p>
                  ) : (
                    <p className="mt-1 text-xs text-slate-500">
                      Give this printer a friendly name (e.g. "Reception" or "Accounting upstairs")
                      so staff can tell it apart from other "{row.printer_name}" printers.
                      Leave blank to use the model name.
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <div className="truncate text-base font-semibold text-slate-900">
                    {row.label || row.printer_name}
                  </div>
                  <button
                    type="button"
                    onClick={startEdit}
                    disabled={anyBusy}
                    aria-label="Rename this printer"
                    title="Rename"
                    className="no-tap-highlight shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </div>
              )}
              {/* When a custom label is set, show the underlying model name
                  in muted text — so staff can still confirm it's the right
                  driver if they need to. */}
              {row.label && !editing ? (
                <div className="mt-0.5 text-xs text-slate-400">{row.printer_name}</div>
              ) : null}
              <div className="mt-1 text-sm text-slate-500">
                On <span className="font-medium">{hostName}</span>
                {row.driver ? <> · {row.driver}</> : null}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <StatusBadge status={row.status} connectivity={row.connectivity} canPrint={canPrint} />
              {canShare ? (
                <button
                  type="button"
                  onClick={onShare}
                  disabled={anyBusy}
                  className="no-tap-highlight rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                >
                  {busy ? "Working…" : "Share with the office"}
                </button>
              ) : null}
              {isShared ? (
                <button
                  type="button"
                  onClick={onUnshare}
                  disabled={anyBusy}
                  className="no-tap-highlight text-xs font-medium text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
                >
                  {busy ? "Working…" : "Stop sharing"}
                </button>
              ) : null}
              {canPrint ? (
                <button
                  type="button"
                  onClick={onTestPrint}
                  disabled={anyBusy || testPrintState?.status === "running"}
                  className="no-tap-highlight text-xs font-medium text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
                >
                  {testPrintState?.status === "running" ? "Sending test page…" : "Test print"}
                </button>
              ) : null}
              {canPrint ? (
                <button
                  type="button"
                  onClick={openFilePicker}
                  disabled={anyBusy || printFileState?.status === "running"}
                  className="no-tap-highlight rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {printFileState?.status === "running" ? "Printing…" : "Print a file"}
                </button>
              ) : null}
              {canReset ? (
                <button
                  type="button"
                  onClick={onResetSettings}
                  disabled={anyBusy || resetSettingsState?.status === "running"}
                  className="no-tap-highlight text-xs font-medium text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
                  title="Fixes a printer whose paper sizes or other settings got stuck (e.g. after reinstalling Windows)"
                >
                  {resetSettingsState?.status === "running" ? "Resetting…" : "Reset printer settings"}
                </button>
              ) : null}
              {/* Hidden picker — opened by the button above. Accept hint
                  mirrors what the agent's dispatcher can actually print
                  (SumatraPDF + Office types). Backend rejects anything
                  outside that whitelist with a clear message anyway. */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.rtf,.txt,.epub"
                onChange={handleFilePicked}
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
              />
              {pendingFile ? (
                <PrintDialog
                  printerName={row.label || row.printer_name}
                  file={pendingFile}
                  capabilities={knownCapabilities(row.capabilities)}
                  onCancel={() => setPendingFile(null)}
                  onPrint={(copies, options) => {
                    onPrintFile(pendingFile, copies, options);
                    setPendingFile(null);
                  }}
                />
              ) : null}
            </div>
          </div>

          {/* Test-print outcome banner. Only renders after the user clicks
              Test print; clears when they dismiss it. */}
          {testPrintState ? (
            <TestPrintResult state={testPrintState} onDismiss={onClearTestPrintState} />
          ) : null}
          {/* Same banner shape for the cloud-routed file print. Stacks
              below the test-print banner when both are visible. */}
          {printFileState ? (
            <TestPrintResult state={printFileState} onDismiss={onClearPrintFileState} />
          ) : null}
          {/* Same banner shape again for the settings-reset outcome. */}
          {resetSettingsState ? (
            <TestPrintResult state={resetSettingsState} onDismiss={onClearResetSettingsState} />
          ) : null}

          {row.is_card_printer ? (
            <CardSettingsPanel
              row={row}
              hostName={hostName}
              hostIsOffline={hostIsOffline}
              disabled={anyBusy}
              onChanged={onChanged}
            />
          ) : null}

          {/* Printer not connected to this PC */}
          {printerDisconnected && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
              <WarningIcon />
              <div>
                <span className="font-medium">Printer not connected</span> — plug it in and turn
                it on, then it will appear as available.
              </div>
            </div>
          )}

          {/* Host offline warning — only when printer is shared */}
          {isShared && hostIsOffline && !printerDisconnected && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <WarningIcon />
              <div>
                <span className="font-medium">Can't print right now</span> —{" "}
                <span className="font-medium">{hostName}</span> is currently off.
                <br />
                <span className="text-amber-700">
                  Turn {hostName} on to use this printer.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function StatusBadge({
  status,
  connectivity,
  canPrint,
}: {
  status: PrinterRow["status"];
  connectivity: PrinterRow["connectivity"];
  canPrint: boolean;
}) {
  if (connectivity === "unplugged") {
    return (
      <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
        Unplugged — plug it back in to use it
      </span>
    );
  }
  if (connectivity === "offline" || connectivity === "error") {
    return (
      <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
        Not connected
      </span>
    );
  }
  if (status === "active") {
    return canPrint ? (
      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-900">
        Ready to print
      </span>
    ) : (
      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
        Shared — host offline
      </span>
    );
  }
  const map: Partial<Record<PrinterRow["status"], { label: string; className: string }>> = {
    detected: { label: "Not shared", className: "bg-slate-200 text-slate-700" },
    pending: { label: "Sharing…", className: "bg-amber-100 text-amber-900" },
    failed: { label: "Setup failed", className: "bg-red-100 text-red-900" },
    removing: { label: "Removing…", className: "bg-slate-200 text-slate-700" },
    removed: { label: "Removed", className: "bg-slate-200 text-slate-500" },
  };
  const meta = map[status];
  if (!meta) return null;
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No printers detected yet</h2>
      <p className="mt-2 text-sm text-slate-600">
        Plug in a printer on a computer running KraetdNet. It'll appear here within a minute.
      </p>
    </div>
  );
}

function printerSortScore(row: PrinterRow): number {
  const disconnected =
    row.connectivity === "offline" || row.connectivity === "error" || row.connectivity === "unplugged";
  if (disconnected) return 100;
  switch (row.status) {
    case "active":
      return 0;
    case "pending":
      return 1;
    case "detected":
      return 2;
    case "failed":
      return 3;
    default:
      return 50;
  }
}

function extractError(
  e: unknown,
  fallback = "Couldn't load printers. We'll try again in a moment.",
): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return fallback;
}

// pollTestPrintUntilDone polls the supplied status endpoint until the
// task reaches a terminal state (succeeded/failed/expired/cancelled)
// or the overall timeout elapses. Returns the final status payload
// either way so the caller can interpret outcome.
//
// `fetchStatus` is parameterized so the same loop works for both the
// test-print and print_file pipelines — they're identical from the
// dashboard's perspective except for the URL path.
async function pollTestPrintUntilDone(
  taskId: string,
  fetchStatus: (taskId: string) => Promise<TestPrintStatus>,
  isAlive?: () => boolean,
): Promise<TestPrintStatus> {
  const deadline = Date.now() + TEST_PRINT_TIMEOUT_MS;
  let last: TestPrintStatus | null = null;
  while (Date.now() < deadline) {
    if (isAlive && !isAlive()) {
      // Component unmounted — stop hitting the backend and let the caller
      // skip its post-poll setState (avoids the React warning + wasted requests).
      throw new DOMException("aborted", "AbortError");
    }
    last = await fetchStatus(taskId);
    if (
      last.status === "succeeded" ||
      last.status === "failed" ||
      last.status === "expired" ||
      last.status === "cancelled"
    ) {
      return last;
    }
    await new Promise((resolve) => setTimeout(resolve, TEST_PRINT_POLL_INTERVAL_MS));
  }
  // Synthesize a timeout status so the UI shows something specific
  // instead of spinning forever.
  if (last) return last;
  return {
    task_id: taskId,
    status: "pending",
    result: {},
    error_message: "",
    created_at: "",
    completed_at: null,
  };
}

// interpretPrintFileStatus is the file-print analogue of
// interpretTestPrintStatus — same outcome mapping, slightly different
// copy ("Printed Report.pdf" instead of "Test page printed").
function interpretPrintFileStatus(status: TestPrintStatus): TestPrintUiState {
  const filename = status.result?.filename || "the file";
  const detail = status.result?.detail ?? "";
  if (status.status !== "succeeded") {
    if (status.error_message?.includes("no handler for task type")) {
      return {
        status: "error",
        message:
          "The host computer is running an older version of KraetdNet. Wait a few minutes for it to update, then try again.",
      };
    }
    const msg = status.error_message || "The print didn't run. Make sure the host PC is on and try again.";
    return { status: "error", message: msg };
  }
  switch (status.result?.outcome) {
    case "ok":
      return { status: "ok", message: `Printed ${filename}! Check the printer tray.` };
    case "in_flight":
      // Engine accepted the job; quick-verify window expired before
      // the spooler confirmed completion. Treat as success because the
      // page is almost certainly coming out — same way the local-UI
      // peer flow does — but soften the wording.
      return {
        status: "ok",
        message: `Sent ${filename} to the printer — it should be on paper in a moment.`,
      };
    case "warn":
      return {
        status: "warn",
        message: detail || `${filename} reached the printer but didn't come out — check for paper, ink, or power.`,
      };
    case "not_found":
      return {
        status: "error",
        message: detail || "The printer didn't accept the job — its name may not match an installed printer on the host PC.",
      };
    case "unsupported":
      return {
        status: "ok",
        message: "Sent to the printer (verification not available on this PC).",
      };
    case "error":
    default:
      return {
        status: "error",
        message: detail || "The printer reported an error. Check the printer.",
      };
  }
}

// interpretResetSettingsStatus maps the reset_printer_settings task's
// result. Unlike test print / print file there's no "outcome" enum — the
// agent reports two independent things: whether the printer queue itself
// was reinstalled, and whether the signed-in user's cached print defaults
// (DEVMODE) could also be cleared (needs someone logged in on that PC).
function interpretResetSettingsStatus(status: TestPrintStatus): TestPrintUiState {
  if (status.status !== "succeeded") {
    if (status.error_message?.includes("no handler for task type")) {
      return {
        status: "error",
        message:
          "The host computer is running an older version of KraetdNet. Wait a few minutes for it to update, then try again.",
      };
    }
    const msg = status.error_message || "The reset didn't run. Make sure the host PC is on and try again.";
    return { status: "error", message: msg };
  }
  const r = status.result ?? {};
  if (!r.reinstalled) {
    return {
      status: "error",
      message: "Couldn't reset the printer — it may not be connected to that PC anymore.",
    };
  }
  if (r.user_reset_ok) {
    return {
      status: "ok",
      message: "Printer settings reset. Close and reopen Word/Excel, then check paper sizes again.",
    };
  }
  return {
    status: "warn",
    message:
      r.user_reset_note ||
      "Printer was reset, but no one is signed in on that PC right now — sign in there and run this again to finish.",
  };
}

// interpretTestPrintStatus maps a terminal task status + agent-reported
// result payload into the inline banner state. Outcome mapping mirrors
// what the agent's print.Outcome reports, but copy is plain-English for
// non-technical owners.
function interpretTestPrintStatus(status: TestPrintStatus): TestPrintUiState {
  const detail = status.result?.detail ?? "";
  if (status.status !== "succeeded") {
    // Task itself never completed cleanly — the agent crashed, the host
    // went offline mid-print, or the task expired before pickup.
    // Old agents (<0.9.3) don't know about test_print and report a
    // "no handler for task type" error; rewrite to a friendlier message
    // so the customer doesn't see internal jargon during the rollout.
    if (status.error_message?.includes("no handler for task type")) {
      return {
        status: "error",
        message: "This computer is running an older version of KraetdNet. Wait a few minutes for it to update, then try again.",
      };
    }
    const msg = status.error_message || "The test print didn't run. Make sure the host PC is on and try again.";
    return { status: "error", message: msg };
  }
  switch (status.result?.outcome) {
    case "ok":
      return { status: "ok", message: "Test page printed! Check the printer tray." };
    case "in_flight":
      // Engine accepted the job and the spooler was still draining it when
      // the verification window closed. The page is almost certainly coming
      // out — same treatment as the print_file flow — so report success with
      // softened wording instead of a false "failed".
      return {
        status: "ok",
        message: "Test page sent to the printer — it should come out in a moment.",
      };
    case "warn":
      return {
        status: "warn",
        message: detail || "Test page reached the printer but it didn't come out — check for paper, ink, or power.",
      };
    case "not_found":
      return {
        status: "error",
        message: detail || "The printer didn't accept the job — its name may not match an installed printer on the host PC.",
      };
    case "unsupported":
      // Non-Windows agent — the engine succeeded but we couldn't verify.
      return {
        status: "ok",
        message: "Sent to the printer (verification not available on this PC).",
      };
    case "error":
    default:
      return {
        status: "error",
        message: detail || "The printer reported an error. Check the printer.",
      };
  }
}

function TestPrintResult({
  state,
  onDismiss,
}: {
  state: TestPrintUiState;
  onDismiss: () => void;
}) {
  const styles =
    state.status === "ok"
      ? "bg-emerald-50 text-emerald-900"
      : state.status === "warn"
        ? "bg-amber-50 text-amber-900"
        : state.status === "error"
          ? "bg-red-50 text-red-900"
          : "bg-slate-50 text-slate-700";
  // jsx-a11y/aria-proptypes wants literal role + aria-live, so we render
  // two parallel branches instead of computing them. role="status" is an
  // implicit live region; "alert" is assertive — screen readers interrupt
  // for errors, stay polite for ok/warn/running.
  const body = (
    <>
      <div>
        <span className="font-medium">
          {state.status === "ok"
            ? "Test page printed"
            : state.status === "warn"
              ? "Test page warning"
              : state.status === "error"
                ? "Test print failed"
                : "Sending test page"}
        </span>{" "}
        — {state.message}
      </div>
      {state.status !== "running" ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss test print result"
          className="no-tap-highlight shrink-0 text-xs font-medium underline hover:opacity-70"
        >
          Dismiss
        </button>
      ) : null}
    </>
  );
  const className = `mt-3 flex items-start justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ${styles}`;
  if (state.status === "error") {
    return (
      <div role="alert" className={className}>
        {body}
      </div>
    );
  }
  return (
    <div role="status" className={className}>
      {body}
    </div>
  );
}

// CardSettingsPanel: an ID-card printer's saved settings. Someone sets the
// card printer up once on its own PC (Printing preferences), then saves
// those settings here; every card printed through KraetdNet, from any PC,
// then comes out with them, so nobody has to set the printer up again
// before each card.
function CardSettingsPanel({
  row,
  hostName,
  hostIsOffline,
  disabled,
  onChanged,
}: {
  row: PrinterRow;
  hostName: string;
  hostIsOffline: boolean;
  disabled: boolean;
  onChanged: () => Promise<void>;
}) {
  const [state, setState] = useState<TestPrintUiState | null>(null);
  const [showHow, setShowHow] = useState(false);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );
  const info = row.card_settings_info ?? null;
  const running = state?.status === "running";
  const printerName = row.label || row.printer_name;
  const saveLabel = info ? `Update from ${hostName}` : `Save the settings from ${hostName}`;
  const summary = info ? describeCardSettings(info) : "";

  async function onSave() {
    setState({ status: "running", message: `Reading the card settings on ${hostName}…` });
    try {
      const ticket = await saveCardSettings(row.id);
      const final = await pollTestPrintUntilDone(ticket.task_id, getTestPrintStatus, () => mountedRef.current);
      if (!mountedRef.current) return;
      setState(interpretSaveCardSettingsStatus(final, hostName));
      await onChanged();
    } catch (e) {
      if (!mountedRef.current || (e instanceof DOMException && e.name === "AbortError")) return;
      setState({ status: "error", message: extractError(e, "Couldn't save the card settings.") });
    }
  }

  async function onClear() {
    if (
      !window.confirm(
        "Stop using the saved card settings? Cards will print with the printer's own settings again.",
      )
    )
      return;
    setState({ status: "running", message: "Removing the saved card settings…" });
    try {
      await clearCardSettings(row.id);
      if (!mountedRef.current) return;
      setState({ status: "ok", message: "Saved card settings removed." });
      await onChanged();
    } catch (e) {
      if (!mountedRef.current) return;
      setState({ status: "error", message: extractError(e, "Couldn't remove the saved card settings.") });
    }
  }

  const stateStyles =
    state?.status === "ok"
      ? "text-emerald-800"
      : state?.status === "warn"
        ? "text-amber-800"
        : state?.status === "error"
          ? "text-red-700"
          : "text-slate-600";

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-slate-900">Card settings</div>
          {info ? (
            <p className="mt-0.5">
              Saved {formatSavedAt(info.saved_at)}
              {info.saved_by ? <> by {info.saved_by}</> : null}
              {summary ? <> · {summary}</> : null}.
              <br />
              <span className="text-slate-500">
                Every card printed to this printer, from any computer, uses these settings.
              </span>
            </p>
          ) : (
            <p className="mt-0.5">
              Not saved yet. Cards print with whatever the printer is set to on {hostName}.
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <button
            type="button"
            onClick={onSave}
            disabled={disabled || running || hostIsOffline}
            title={hostIsOffline ? `Turn ${hostName} on first` : undefined}
            className="no-tap-highlight rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {running ? "Working…" : saveLabel}
          </button>
          {info ? (
            <button
              type="button"
              onClick={onClear}
              disabled={disabled || running}
              className="no-tap-highlight text-xs font-medium text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
            >
              Stop using saved settings
            </button>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowHow((v) => !v)}
        aria-expanded={showHow}
        className="no-tap-highlight mt-2 text-xs font-medium text-slate-600 underline hover:text-slate-900"
      >
        {showHow ? "Hide how it works" : "How do I set this up?"}
      </button>
      {showHow ? (
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-slate-600">
          <li>
            On <span className="font-medium">{hostName}</span>, open Start → Settings → Bluetooth &amp;
            devices → Printers &amp; scanners → <span className="font-medium">{row.printer_name}</span> →
            Printing preferences.
          </li>
          <li>
            Set everything the way your cards need it (card size, ribbon, which sides, overlay,
            orientation) and click OK.
          </li>
          <li>
            Come back here and click <span className="font-medium">{saveLabel}</span>.
          </li>
          <li>
            Print cards as usual from any computer: pick &ldquo;{printerName} (KraetdNet)&rdquo;. The saved
            settings are used every time, and cards print to the very edge.
          </li>
        </ol>
      ) : null}
      {state ? (
        state.status === "error" ? (
          <p role="alert" className={`mt-2 text-xs ${stateStyles}`}>
            {state.message}
          </p>
        ) : (
          <p role="status" className={`mt-2 text-xs ${stateStyles}`}>
            {state.message}
          </p>
        )
      ) : null}
    </div>
  );
}

function describeCardSettings(info: CardSettingsInfo): string {
  const s = info.summary ?? {};
  const parts: string[] = [];
  if (s.card_size) parts.push(s.card_size);
  if (s.orientation) parts.push(s.orientation === "landscape" ? "Landscape" : "Portrait");
  if (s.color) parts.push(s.color === "color" ? "Color" : "Black and white");
  if (s.sides) parts.push(s.sides === "both" ? "Both sides" : "One side");
  return parts.join(" · ");
}

function formatSavedAt(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `on ${d.toLocaleString("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function interpretSaveCardSettingsStatus(status: TestPrintStatus, hostName: string): TestPrintUiState {
  if (status.status !== "succeeded") {
    if (status.error_message?.includes("no handler for task type")) {
      return {
        status: "error",
        message: `${hostName} is running an older version of KraetdNet. Wait a few minutes for it to update, then try again.`,
      };
    }
    if (status.status === "pending" || status.status === "running") {
      return { status: "warn", message: `${hostName} didn't answer yet. Make sure it's on, then try again.` };
    }
    // The agent's messages are plain sentences in Go error style (lowercase, no full stop).
    const raw = status.error_message?.trim();
    return {
      status: "error",
      message: raw
        ? `${raw[0].toUpperCase()}${raw.slice(1)}${raw.endsWith(".") ? "" : "."}`
        : `Couldn't read the card settings on ${hostName}. Make sure it's on and try again.`,
    };
  }
  if (!status.result?.saved) {
    return {
      status: "error",
      message: status.result?.detail || `${hostName} didn't send usable settings. Try again.`,
    };
  }
  if (status.result.source === "printer_defaults") {
    return {
      status: "ok",
      message:
        "Saved. Nobody was signed in on that computer (or nobody had changed its settings), so the printer's default settings were saved.",
    };
  }
  return { status: "ok", message: "Saved. Every card printed to this printer now uses these settings." };
}

// The printer's capabilities when its PC has reported them (the API sends {} until then).
function knownCapabilities(c: PrinterRow["capabilities"]): PrinterCapabilities | null {
  return c && "sizes" in c && Array.isArray(c.sizes) ? (c as PrinterCapabilities) : null;
}
